import {
  ensureArray,
  formatDMYWithTime,
  RiskFactors,
  SupportNeedRiskFactorValue,
  titleCaseFromCaps,
} from '@lbcamden/shared-utils';

import { GlobalStore } from '~/api/tinybase/GlobalStore';
import { tempNumericId } from '~/common/tinybase/utils';
import {
  ContactDetailsArrayKey,
  ContactDetailsItem,
  ContactType,
  PcfraPerson,
  Person,
  PersonContactDetails,
  PcfraStatus,
  PersonRiskFactorItem,
  PersonRiskFactors,
  RiskFactorKey,
  PersonAttributeItem,
  PersonAttributeCategoryCode,
  PersonAttributes,
} from '~/common/types/people';

import { User } from '../types/app';
import { BaseResident } from '../types/residentsReport';

export const detailsArrayKeyToNewItemTypeMap = new Map<ContactDetailsArrayKey, ContactType>([
  ['emailAddresses', 'EMAIL'],
  ['mobileNumbers', 'MOB'],
  ['smsOnlyNumbers', 'SMSTEXTNO'],
  ['otherPhoneNumbers', 'TELEPHONE'],
]);

export const detailsArrayKeys = [...detailsArrayKeyToNewItemTypeMap.keys()];

export const getPersonRiskFactorItemId = (personId: number, key: RiskFactorKey) =>
  `${personId}_${key}`;

const contactDetailsByType = <TTypes extends [ContactType, ...ContactType[]]>(
  contactDetails: ContactDetailsItem[],
  types: TTypes,
) =>
  contactDetails.filter(({ type }) => types.includes(type)) as ContactDetailsItem<TTypes[number]>[];

const attributesByCategory = (
  attributes: PersonAttributeItem[],
  categoryCode: PersonAttributeCategoryCode | PersonAttributeCategoryCode[],
) =>
  attributes.filter((item) =>
    ensureArray(categoryCode).includes(item.categoryCode as PersonAttributeCategoryCode),
  );

export const mergePersonWithContactDetails = <TPerson extends object>(
  person: TPerson,
  contactDetails: ContactDetailsItem[],
): TPerson & PersonContactDetails => ({
  ...person,
  emailAddresses: contactDetailsByType(contactDetails, ['EMAIL']),
  mobileNumbers: contactDetailsByType(contactDetails, ['MOB']),
  smsOnlyNumbers: contactDetailsByType(contactDetails, ['SMSTEXTNO']),
  otherPhoneNumbers: contactDetailsByType(contactDetails, ['HOMETEL', 'TELEPHONE']),
});

export const mergePersonWithAttributes = <TPerson extends object>(
  person: TPerson,
  attributes: PersonAttributeItem[],
): TPerson & PersonAttributes => ({
  ...person,
  supportNeeds: attributesByCategory(attributes, [
    PersonAttributeCategoryCode.SupportNeed,
    PersonAttributeCategoryCode.SupportNeedRiskFactor,
  ]),
  reasonableAdjustments: attributesByCategory(
    attributes,
    PersonAttributeCategoryCode.ReasonableAdjustment,
  ),
  communicationAdjustments: attributesByCategory(
    attributes,
    PersonAttributeCategoryCode.CommunicationAdjustment,
  ),
  communicationPreferences: attributesByCategory(
    attributes,
    PersonAttributeCategoryCode.CommunicationPreference,
  ),
});

export const mergePersonWithRiskFactors = <TPerson extends PcfraPerson | BaseResident>(
  person: TPerson,
  riskFactors: PersonRiskFactorItem[],
  supportNeedRiskFactors?: SupportNeedRiskFactorValue[],
): TPerson & PersonRiskFactors => ({
  ...person,
  supportNeedRiskFactors,
  riskFactors: riskFactors.reduce(
    (factors, { key, value, description }) => ({
      ...factors,
      [key]: { value, description },
    }),
    <RiskFactors>{},
  ),
});

export const getTempContactDetailItem = (
  arrayKey: ContactDetailsArrayKey,
  personId: number,
  value: string,
) => ({
  personId,
  value,
  // Will be overwritten when the changes are applied in NEC
  detailsId: tempNumericId(),
  type: detailsArrayKeyToNewItemTypeMap.get(arrayKey)!,
});

export const fullNameHistoricSuffix = '(historic)';

export const buildGetPersonNameFn =
  (referenceListDisplayValue: GlobalStore['referenceListDisplayValue']) =>
  ({
    title,
    forename,
    surname,
    ...fields
  }: Pick<Person | PcfraPerson, 'title' | 'forename' | 'surname'> & { isHistoric?: boolean }) =>
    [
      referenceListDisplayValue('TITLE', title),
      titleCaseFromCaps(`${forename} ${surname}`),
      (fields as Person).isHistoric && fullNameHistoricSuffix,
    ]
      .filter(Boolean)
      .join(' ');

export const getFullPcfraStatusComment = (
  prevStatus: PcfraStatus,
  newStatus: PcfraStatus,
  { name, email }: User,
) => {
  const now = formatDMYWithTime(new Date());
  const userName = `${name} (${email})`;
  const updatedByAt = `*By ${userName} at ${now}*`;

  const defaultCommentsMap: Partial<Record<PcfraStatus, string>> = {
    [PcfraStatus.Necessary]: 'PCFRA was deemed necessary',
    [PcfraStatus.Unnecessary]: 'PCFRA was deemed unnecessary',
    [PcfraStatus.Drafting]:
      prevStatus === PcfraStatus.Submitted ? 'PCFRA re-opened for editing' : 'PCFRA started',
    [PcfraStatus.Submitted]: 'PCFRA submitted for approval',
    [PcfraStatus.Active]:
      prevStatus === PcfraStatus.Active ? 'PCFRA reconfirmed' : 'PCFRA approved and set active',
  };

  const statusComment = defaultCommentsMap[newStatus] || 'Status updated';

  return `${statusComment}.\n\n${updatedByAt}`;
};
